import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import usePersistedState from '../components/utils/mapPersistedState';
import { Stack, Box, Grid, Tab, Slider, Typography, Button, IconButton, Alert } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Oval } from 'react-loader-spinner';
import HouseIcon from '@mui/icons-material/House';
import "ol/ol.css";
import Map from "ol/Map";
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZSource from 'ol/source/XYZ'
import VectorTileLayer from 'ol/layer/VectorTile'
import VectorTileSource from 'ol/source/VectorTile'
import MVT from 'ol/format/MVT';
import Feature from 'ol/Feature';
// import { extend } from 'ol/extent';
import { defaults, Draw } from 'ol/interaction';
import { Style, Stroke, Fill } from "ol/style";
import GeoJSON from 'ol/format/GeoJSON';
import { intersect } from "@turf/intersect";
import { featureCollection } from "@turf/helpers";
import { generateBuckets, BucketTypes } from "geobuckets";
import { SA4VectorTileEndpoint, StateVectorTileEndpoint } from "../config/backendConfig";
import { apiRequest } from "../services/utils/HttpUtils";
import { useQuery } from "@tanstack/react-query";
import { RetrieveAllMapList, RetrieveFiscalYears, RetrieveStateSA4MapInvestment, RetrieveSA4OverTimeMapInvestment, RetrieveSA4PerCapitaMapInvestment } from "../graphql/queries_nationalMap";
import { getUserInfo } from "../graphql/queries_user_info";
import InvestmentChart from "../components/national-map/InvestmentChart";
import LabourShortageChart from "../components/national-map/LabourShortageChart";
import NonLabourDemandChart from "../components/national-map/NonLabourDemandChart";
import MapPlotFilterList from "../components/utils/mapPlotFilterList";
import {
  nationalMapFiltersConfigList
} from "../components/national-map/nationalMapFiltersConfig";
import { sa4Caveat, stateCaveat } from "../assets/texts/pageTextContent";

export default function NationalMap() {

  // Dates
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // GeoJSON object
  const geojson = new GeoJSON();

  // Authentication
  const { instance, accounts } = useMsal();
  const { data: userInfo } = getUserInfo();

  const isMounted = useRef(false);

  // References to html components
  const mapContainer = useRef<any>();
  const legendContainer = useRef<any>();
  const infoContainer = useRef<any>();
  const fiscalYearRangeContainer = useRef<any>();
  const fiscalYearRangeRef = useRef<any>();
  const calendarYearContainer = useRef<any>();
  const calendarYearRef = useRef<any>();
  const emptyYearContainer = useRef<any>();
  const mapLoader = useRef<any>();
  const alertContainer = useRef<any>();

  // Disable/Enable components
  const [sa4MapTab, setSa4MapTab] = useState(true);
  const [stateMapTab, setStateMapTab] = useState(true);
  const [overTimeMapTab, setOverTimeMapTab] = useState(true);
  const [perCapitaMapTab, setPerCapitaMapTab] = useState(true);
  const [investmentTab, setInvestmentTab] = useState(true);
  const [labourTab, setLabourTab] = useState(true);
  const [nonLabourTab, setNonLabourTab] = useState(true);
  const [fiscalYearsSlider, setFiscalYearsSlider] = useState(true);
  const [calendarYearsSlider, setCalendarYearsSlider] = useState(true);
  const [caveat, setCaveat] = useState('');

  // Map values refs
  const sa4MapInvestmentValuesRef = useRef<any[]>([]);
  const stateMapInvestmentValuesRef = useRef<any[]>([]);
  const sa4OverTimeValuesRef = useRef<any[]>([]);
  const sa4PerCapitaValuesRef = useRef<any[]>([]);

  // Extent state
  // const [tileCount, setTileCount] = useState(0);
  // const [updateExtent, setUpdateExtent] = useState(false);
  // const [currentExtent, setCurrentExtent] = usePersistedState<number[]>('currentExtent', []);

  // Attribute lists state
  const [listsDisabled, setListsDisabled] = useState(true);
  const [mapPlotQueryProps, setMapPlotQueryProps] = usePersistedState<any>('mapPlotQueryProps', {
    inState: ["ALL"],
    inLocationType: ["ALL"],
    inInvestmentTier: ["ALL"],
    inCommonwealthFunded: ["ALL"],
    inPipeline: ["ALL"],
    inProjectPhase: ["ALL"],
    inSuperSector: ["ALL"],
    inMasterType: ["ALL"],
    inProjectType: ["ALL"]
  });

  const [labourPlotQueryProps, setLabourPlotQueryProps] = usePersistedState('labourPlotQueryProps', {
    inLvl1Group: ["ALL"],
    inResourceGroup: ["ALL"]
  });

  const [nonLabourPlotQueryProps, setNonLabourPlotQueryProps] = usePersistedState<any>('nonLabourPlotQueryProps', {
    inPLEMGroup: ["ALL"],
    inResourceGroup: ["ALL"]
  });

  // Query values state
  const [queryVariables, setQueryVariables] = usePersistedState<any>('queryVariables', {
    inState: "",
    inLocationType: "",
    inInvestmentTier: "",
    inCommonwealthFunded: "",
    inPipeline: "",
    inProjectPhase: "",
    inSuperSector: "",
    inMasterType: "",
    inProjectType: "",
    inPLEMGroup: "",
    inLvl1Group: "",
    inResourceGroup: "",
    inStartYear: 0,
    inEndYear: 0,
    inCalendarYear: currentYear,
    inYear: currentYear,
    inMonth: currentMonth
  });

  // Set component updates
  const [updates, setUpdates] = useState({
    isFiscalYears: false,
    isCalendarYear: false,
    isRegion: false,
    isOther: false,
    isLabour: false,
    isNonLabour: false
  });

  // Update charts state
  const [updateInvestmentCharts, setUpdateInvestmentCharts] = useState(0);
  const [updateLabourCharts, setUpdateLabourCharts] = useState(0);
  const [updateNonLabourCharts, setUpdateNonLabourCharts] = useState(0);

  // State to manage Alert properties
  const [alertOptions, setAlertOptions] = useState({
    show: false,
    type: 'info' as 'success' | 'info' | 'warning' | 'error',
    content: ''
  });

  // Legend/Map colours
  const map_colours = ["#b3e0a6", "#ffdf8e", "#eeab26", "#ff3b29", "#aa0f40"];

  const { data: allMapLists, status: allMapListsStatus, fetchStatus: allMapListsFetchStatus, error: allMapListsError, refetch: refetchAllMapLists } = useQuery({
    queryKey: ["ALLMapLists"],
    queryFn: () => {
      return apiRequest(
        {
          query: RetrieveAllMapList,
          variables: queryVariables,
          instance: instance,
          accounts: accounts
        })
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!userInfo
  });

  const { data: fiscalYears, status: fiscalYearsStatus, fetchStatus: fiscalYearsFetchStatus, error: fiscalYearsError } = useQuery({
    queryKey: ["FiscalYears"],
    queryFn: () => {
      return apiRequest(
        {
          query: RetrieveFiscalYears,
          instance: instance,
          accounts: accounts
        })
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!userInfo
  });

  const { data: stateSa4MapInvestmentValues, status: stateSa4MapInvestmentStatus, fetchStatus: stateSa4MapInvestmentFetchStatus, error: stateSa4MapInvestmentError, refetch: refetchStateSa4MapInvestmentValues } = useQuery({
    queryKey: ["StateSA4MapInvestmentValues"],
    queryFn: () => {
      return apiRequest(
        {
          query: RetrieveStateSA4MapInvestment,
          variables: queryVariables,
          instance: instance,
          accounts: accounts
        })
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!userInfo
  });

  const { data: sa4OverTimeValues, status: sa4OverTimeStatus, fetchStatus: sa4OverTimeFetchStatus, error: sa4OverTimeError, refetch: refetchSA4OverTimeValues } = useQuery({
    queryKey: ["SA4OverTimeValues"],
    queryFn: () => {
      return apiRequest(
        {
          query: RetrieveSA4OverTimeMapInvestment,
          variables: queryVariables,
          instance: instance,
          accounts: accounts
        })
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!userInfo
  });

  const { data: sa4PerCapitaValues, status: sa4PerCapitaStatus, fetchStatus: sa4PerCapitaFetchStatus, error: sa4PerCapitaError, refetch: refetchSA4PerCapitaValues } = useQuery({
    queryKey: ["SA4PerCapitaValues"],
    queryFn: () => {
      return apiRequest(
        {
          query: RetrieveSA4PerCapitaMapInvestment,
          variables: queryVariables,
          instance: instance,
          accounts: accounts
        })
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: Infinity,
    enabled: !!userInfo
  });

  const updateAlert = (visibility: 'visible' | 'hidden', type: 'success' | 'info' | 'warning' | 'error', content: string) => {
    setAlertOptions({
      show: visibility === 'visible',
      type: type,
      content: content
    });
  };

  // Map style updater
  const updateStyle = async (values) => {
    if (mapTabIndex.current === 2) {
      // Over time map
      return f => {
        let colour: string | undefined;
        if (f.get('value') >= 0 && f.get('value') <= 1) { colour = map_colours[0]; }
        else if (f.get('value') > 1 && f.get('value') <= 2) { colour = map_colours[1]; }
        else if (f.get('value') > 2 && f.get('value') <= 3) { colour = map_colours[2]; }
        else if (f.get('value') > 3) { colour = map_colours[3]; }

        if (colour) {
          return new Style({
            stroke: new Stroke({
              color: 'black',
              width: 0.7
            }),
            fill: new Fill({
              color: colour
            })
          });
        }
      }
    } else {
      const results = await classify(values);
      if (results.length <= 5) {
        return f => {
          let colour: string | undefined;
          results.forEach((r, i) => {
            if (r === f.get('value')) {
              colour = map_colours[i];
            }
          });

          if (colour) {
            return new Style({
              stroke: new Stroke({
                color: 'black',
                width: 0.7
              }),
              fill: new Fill({
                color: colour
              })
            });
          }
        }
      } else {
        return f => {
          let colour: string | undefined;
          for (let i = 0; i < 5; i++) {
            if (f.get('value') >= results[i] && f.get('value') <= results[i + 1]) {
              colour = map_colours[i];
            }
          };

          if (colour) {
            return new Style({
              stroke: new Stroke({
                color: 'black',
                width: 0.7
              }),
              fill: new Fill({
                color: colour
              })
            });
          }
        }
      }
    }
  };

  const formatValue = (value, type) => {
    if (type === 'percent') {
      return new Intl.NumberFormat("en-AU", { style: "percent", maximumFractionDigits: 0 }).format(value);
    } else if (type === 'currency') {
      return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", currencyDisplay: "narrowSymbol", notation: "compact", compactDisplay: "short" }).format(value);
    } else {
      return value;
    }
  };

  // Legend style updater
  const [legendHeading, setLegendHeading] = useState("");
  const [bins, setBins] = useState(Array<string>);
  const updateLegend = async (values) => {
    if (mapTabIndex.current === 2) {
      // Over time map
      setBins(['0% - 100%', '100% - 200%', '200% - 300%', '> 300%']);
    } else {
      const results = await classify(values);
      let binArr: string[] = [];
      if (results.length <= 5) {
        results.forEach((r: number) => {
          binArr.push(formatValue(r, 'currency'));
        });
      } else {
        for (let i = 0; i < 5; i++) {
          binArr.push(formatValue(results[i], 'currency') + " - " + formatValue(results[i + 1], 'currency'))
        };
      }
      setBins(binArr);
    }
  };

  const classify = async (values) => {
    let results: number[] = [];
    if (values.length > 5) {
      if (mapTabIndex.current === 3) {
        // Per capita map
        results = await generateBuckets(BucketTypes.JNK, values, 5);
      } else {
        results = await generateBuckets(BucketTypes.GPG, values, 5);
      }
    } else {
      results = values.sort((a, b) => {
        return a - b;
      });
    }

    // Due to the classification producing
    // numbers with a large precision, 
    // need to make sure that
    // the smallest/largest values are used at
    // the beginning/end of results array.
    results[0] = Math.min(...values);
    results[results.length - 1] = Math.max(...values);

    return results;
  };

  // SA4 map feature styler
  const setSa4Style = (values) => {
    updateStyle(values).then((s) => {
      (map.getLayers().item(1) as VectorTileLayer).setStyle(s);
    });
  };

  // SA4 map layer
  const sa4Layer = new VectorTileLayer({
    visible: false,
    zIndex: 0,
    source: new VectorTileSource<Feature>({
      format: new MVT({ idProperty: 'oid', featureClass: Feature }),
      url: SA4VectorTileEndpoint
    }),
    style: null
  });

  // SA4 map source data updater
  const sa4Source = sa4Layer.getSource()!;
  // sa4Source?.on('tileloadstart', (e) => {
  //   setTileCount((count) => count + 1);
  // });
  // sa4Source?.on('tileloaderror', (e) => {
  //   setTileCount((count) => count + 1);
  // });
  sa4Source?.on('tileloadend', (e) => {
    // @ts-ignore
    const features = e.tile.getFeatures();
    // let bounds: number[] = [];
    features.forEach((f) => {
      f.set('value', 0, true);
      if (mapTabIndex.current === 0) {
        const found = sa4MapInvestmentValuesRef.current.find(e => e['LocationType'] === f.get('sa4_name21'));
        if (found) {
          f.set('value', found['Value'], true);
          // bounds.length > 0 ? bounds = extend(bounds, f.getGeometry().getExtent()) : bounds = f.getGeometry().getExtent();
        }
      } else if (mapTabIndex.current === 2) {
        const found = sa4OverTimeValuesRef.current.find(e => e['LocationType'] === f.get('sa4_name21'));
        if (found) {
          f.set('value', found['Total_Next'] / found['Total_Prev'], true);
          f.set('prev', found['Total_Prev'], true);
          f.set('next', found['Total_Next'], true);
          // bounds.length > 0 ? bounds = extend(bounds, f.getGeometry().getExtent()) : bounds = f.getGeometry().getExtent();
        } else {
          f.set('value', -1.0, true);
        }
      } else if (mapTabIndex.current === 3) {
        const found = sa4PerCapitaValuesRef.current.find(e => e['LocationType'] === f.get('sa4_name21'));
        if (found) {
          f.set('value', found['Value'] / found['Population'], true);
          f.set('inv', found['Value'], true);
          f.set('pop', found['Population'], true);
          // bounds.length > 0 ? bounds = extend(bounds, f.getGeometry().getExtent()) : bounds = f.getGeometry().getExtent();
        }
      }
    });
    // setCurrentExtent(bounds);
    // setTileCount((count) => count + 1);
  });

  // State map feature styler
  const setStateStyle = (values) => {
    updateStyle(values).then((s) => {
      (map.getLayers().item(2) as VectorTileLayer).setStyle(s);
    });
  };

  // State map layer
  const stateLayer = new VectorTileLayer({
    visible: false,
    zIndex: 1,
    source: new VectorTileSource<Feature>({
      format: new MVT({ idProperty: 'oid', featureClass: Feature }),
      url: StateVectorTileEndpoint
    }),
    style: null
  });

  // State map source data updater
  const stateSource = stateLayer.getSource()!;
  // stateSource?.on('tileloadstart', (e) => {
  //   setTileCount((count) => count + 1);
  // });
  // stateSource?.on('tileloaderror', (e) => {
  //   setTileCount((count) => count + 1);
  // });
  stateSource?.on('tileloadend', (e) => {
    // @ts-ignore
    const features = e.tile.getFeatures();
    // let bounds: number[] = [];
    features.forEach((f) => {
      const found = stateMapInvestmentValuesRef.current.find(e => e['State'] === f.get('ste_abbr'));
      if (found) {
        f.set('value', found['Value'], true);
        // bounds.length > 0 ? bounds = extend(bounds, f.getGeometry().getExtent()) : bounds = f.getGeometry().getExtent();
      }
    });
    // setCurrentExtent(bounds);
    // setTileCount((count) => count + 1);
  });

  // const getTileCount = (source) => {
  //   const size = map.getSize();
  //   const view = map.getView();
  //   const extent = view.calculateExtent(size);
  //   const zoom = view.getZoom();
  //   let count = 0;
  //   console.log(source.getTileGrid());
  //   source.getTileGrid().forEachTileCoord(extent, zoom, () => {
  //     console.log('count');
  //     ++count;
  //  });
  //   return count
  // }

  // SA4 map hover feature layer
  const sa4Hover = new VectorTileLayer({
    visible: false,
    zIndex: 3,
    renderMode: 'vector',
    // @ts-ignore
    source: sa4Source,
    style: (f) => {
      // @ts-ignore
      if (f.getId() in sa4Selection) {
        return hoverStyle;
      }
    },
  });

  // State map hover feature layer
  const stateHover = new VectorTileLayer({
    visible: false,
    zIndex: 4,
    renderMode: 'vector',
    // @ts-ignore
    source: stateSource,
    style: (f) => {
      // @ts-ignore
      if (f.getId() in stateSelection) {
        return hoverStyle;
      }
    },
  });

  // Polygon map selection interaction
  const drawPolygon = new Draw({
    type: 'Polygon',
    style: {
      'stroke-color': '#6666FF',
      'stroke-width': 1.5,
      'fill-color': 'rgba(102, 102, 255, 0.25)',
    }
  });

  // On drawing completion, select the
  // state or sa4 features
  drawPolygon.on('drawend', (e) => {
    const polygon1 = geojson.writeFeatureObject(e.feature);
    if (polygon1) {
      // Filter either the SA4 and State layer
      if ([0, 2, 3].indexOf(mapTabIndex.current) >= 0) {
        let locationTypes = '';
        const extent = sa4Source.getTileGrid()!.getExtent();
        sa4Layer.getFeaturesInExtent(extent).forEach((feature: Feature) => {
          const polygon2 = geojson.writeFeatureObject(feature);
          const intersects = intersect(featureCollection([polygon2, polygon1]));
          if (intersects) {
            const sa4Region = feature.get('sa4_name21');
            if (!locationTypes.includes(sa4Region)) {
              locationTypes = locationTypes == '' ? sa4Region : locationTypes + '|' + sa4Region;
            }
          }
        });
        setQueryVariables(queryVariables => ({
          ...queryVariables,
          inLocationType: locationTypes
        }));
      } else if (mapTabIndex.current == 1) {
        let states = '';
        const extent = stateSource.getTileGrid()!.getExtent();
        stateLayer.getFeaturesInExtent(extent).forEach((feature: Feature) => {
          const polygon2 = geojson.writeFeatureObject(feature);
          const intersects = intersect(featureCollection([polygon2, polygon1]));
          if (intersects) {
            const state = feature.get('ste_abbr');
            if (!queryVariables.inState.includes(state)) {
              states = states == '' ? state : states + '|' + state;
            }
          }
        });
        setQueryVariables(queryVariables => ({
          ...queryVariables,
          inState: states
        }));
      }
      setUpdates({
        isFiscalYears: false,
        isCalendarYear: false,
        isRegion: true,
        isOther: false,
        isLabour: false,
        isNonLabour: false
      });
    }
  });

  // The map object
  const [map] = useState(
    new Map({
      view: new View({
        enableRotation: false,
        center: [14983933, -3027317],
        zoom: 4,
        extent: [10825600, -5717800, 19142200, -336800],
        minZoom: 4,
        maxZoom: 10
      }),
      layers: [
        new TileLayer({
          source: new XYZSource({
            url: 'https://{1-4}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png?key=cb1_2ed4_1_c07d3ea1e423045fc646ca1d',
          })
        }), sa4Layer, stateLayer, sa4Hover, stateHover
      ],
      interactions: (defaults()).extend([drawPolygon])
    })
  );

  // Map layer visibility toggle
  const setLayerVisibility = () => {
    const layers = map.getLayers();
    if ([0, 2, 3].indexOf(mapTabIndex.current) >= 0) {
      if (mapTabIndex.current === 0) {
        setLegendHeading("Total investment per SA4");
        setCaveat(sa4Caveat);
        const values = sa4MapInvestmentValuesRef.current.map(a => a.Value);
        setSa4Style(values);
        updateLegend(values);
      } else if (mapTabIndex.current === 2) {
        setLegendHeading("Investment over time");
        setCaveat(sa4Caveat);
        const values = sa4OverTimeValuesRef.current.map(a => a.Total_Next / a.Total_Prev);
        setSa4Style(values);
        updateLegend(values);
      } else if (mapTabIndex.current === 3) {
        setLegendHeading("Investment per capita");
        // setCaveat(perCapitaCaveat);
        const values = sa4PerCapitaValuesRef.current.map(a => a.Value / a.Population);
        setSa4Style(values);
        updateLegend(values);
      }

      // Update layer visibility
      (layers.item(2) as VectorTileLayer).setVisible(false);
      (layers.item(1) as VectorTileLayer).setVisible(true);
      (layers.item(4) as VectorTileLayer).setVisible(false);
      (layers.item(3) as VectorTileLayer).setVisible(true);
      (layers.item(1) as VectorTileLayer).getSource()!.changed();
      (layers.item(3) as VectorTileLayer).changed();
    } else if (mapTabIndex.current == 1) {
      setLegendHeading("Total investment per State");
      setCaveat(stateCaveat);
      const values = stateMapInvestmentValuesRef.current.map(a => a.Value);
      setStateStyle(values);
      updateLegend(values);

      // Update layer visibility
      (layers.item(1) as VectorTileLayer).setVisible(false);
      (layers.item(2) as VectorTileLayer).setVisible(true);
      (layers.item(3) as VectorTileLayer).setVisible(false);
      (layers.item(4) as VectorTileLayer).setVisible(true);
      (layers.item(2) as VectorTileLayer).getSource()!.changed();
      (layers.item(4) as VectorTileLayer).changed();
    }
    mapLoader.current!.style.display = 'none';
    displayTimeSlider();
  };

  const displayTimeSlider = () => {
    if ([0, 1].indexOf(mapTabIndex.current) >= 0) {
      calendarYearContainer.current!.style.display = 'none';
      emptyYearContainer.current!.style.display = 'none';
      fiscalYearRangeContainer.current!.style.display = 'flex';
    } else if (mapTabIndex.current == 2) {
      calendarYearContainer.current!.style.display = 'none';
      fiscalYearRangeContainer.current!.style.display = 'none';
      emptyYearContainer.current!.style.display = 'flex';
    } else if (mapTabIndex.current == 3) {
      fiscalYearRangeContainer.current!.style.display = 'none';
      emptyYearContainer.current!.style.display = 'none';
      calendarYearContainer.current!.style.display = 'flex';
    }
  };

  const setDefaultView = () => {
    map.getView().animate({ zoom: 4 }, { center: [14983933, -3027317] }, { duration: 1000 });
  };

  const resetComponents = () => {
    // setTileCount(0);
    // setUpdateExtent(false);
    // setCurrentExtent([]);
    setMapPlotQueryProps({
      inState: ["ALL"],
      inLocationType: ["ALL"],
      inInvestmentTier: ["ALL"],
      inCommonwealthFunded: ["ALL"],
      inPipeline: ["ALL"],
      inProjectPhase: ["ALL"],
      inSuperSector: ["ALL"],
      inMasterType: ["ALL"],
      inProjectType: ["ALL"]
    });
    setLabourPlotQueryProps({
      inLvl1Group: ["ALL"],
      inResourceGroup: ["ALL"]
    });
    setNonLabourPlotQueryProps({
      inPLEMGroup: ["ALL"],
      inResourceGroup: ["ALL"]
    });
    setQueryVariables({
      inState: "",
      inLocationType: "",
      inInvestmentTier: "",
      inCommonwealthFunded: "",
      inPipeline: "",
      inProjectPhase: "",
      inSuperSector: "",
      inMasterType: "",
      inProjectType: "",
      inPLEMGroup: "",
      inLvl1Group: "",
      inResourceGroup: "",
      inStartYear: 0,
      inEndYear: 0,
      inCalendarYear: currentYear,
      inYear: currentYear,
      inMonth: currentMonth
    });
    setDefaultView();
    setFiscalYearsSlider(true);
    setFiscalYearRange([fiscalYrs[0], fiscalYrs[fiscalYrs.length - 1]]);
    setCalendarYearsSlider(true);
    setCalendarYear(currentYear);
    setUpdates({
      isFiscalYears: false,
      isCalendarYear: false,
      isRegion: false,
      isOther: false,
      isLabour: false,
      isNonLabour: false
    });
  };

  const updateComponents = () => {
    const isAll = Object.values(updates).every((i) => i === false)
    mapLoader.current!.style.display = 'flex';
    // isAll ? setUpdateExtent(false) : setUpdateExtent(true);

    if (isAll || updates.isRegion || updates.isOther) {
      setListsDisabled(true);
      refetchAllMapLists();
      setOverTimeMapTab(true);
      refetchSA4OverTimeValues();
    }

    if (isAll || updates.isFiscalYears || updates.isRegion || updates.isOther) {
      setSa4MapTab(true);
      setStateMapTab(true);
      refetchStateSa4MapInvestmentValues();
      setInvestmentTab(true);
      setUpdateInvestmentCharts((updateInvestmentCharts) => updateInvestmentCharts + 1);
    }

    if (isAll || updates.isCalendarYear || updates.isRegion || updates.isOther) {
      setPerCapitaMapTab(true);
      refetchSA4PerCapitaValues();
    }

    if ((isAll || updates.isFiscalYears || updates.isRegion || updates.isLabour) && !updates.isNonLabour) {
      setLabourTab(true);
      setUpdateLabourCharts((updateLabourCharts) => updateLabourCharts + 1);
    }

    if ((isAll || updates.isFiscalYears || updates.isRegion || updates.isOther || updates.isNonLabour) && !updates.isLabour) {
      setNonLabourTab(true);
      setUpdateNonLabourCharts((updateNonLabourCharts) => updateNonLabourCharts + 1);
    }
  };

  // Tab state updater
  const [tabMapIndex, setTabMapIndex] = usePersistedState<number>('tabMapIndex', 0);
  const mapTabIndex = useRef(tabMapIndex);
  const handleMapChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabMapIndex(newValue);
    mapTabIndex.current! = newValue;
    setLayerVisibility();
  };

  const [tabChartIndex, setTabChartIndex] = usePersistedState<number>('tabChartIndex', 0);
  const handleChartChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabChartIndex(newValue);
  };

  // Fix for removing the hover popup
  // when the mouse leaves the map component
  const handleMapLeave = () => {
    // @ts-ignore
    map.dispatchEvent({ type: 'pointermove', pixel: [] });
  };

  // Prevent mouse events on the legend
  // container.
  const handleLegendHover = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  // Fiscal year time slider
  const minRange = 1;
  const [fiscalYrs, setFiscalYrs] = useState<number[]>([]);
  const [fiscalYearRange, setFiscalYearRange] = usePersistedState<number[]>('fiscalYearRange', []);
  const handleYearRangeChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (newValue[1] - newValue[0] < minRange) {
      if (activeThumb === 0) {
        const clamped = Math.min(newValue[0], fiscalYrs[fiscalYrs.length - 1] - minRange);
        setFiscalYearRange([clamped, clamped + minRange]);
      } else {
        const clamped = Math.max(newValue[1], minRange);
        setFiscalYearRange([clamped - minRange, clamped]);
      }
    } else {
      setFiscalYearRange(newValue as number[]);
    }
  };

  const handleYearRangeSet = (event: React.SyntheticEvent | Event, value: number | number[]) => {
    setQueryVariables(queryVariables => ({
      ...queryVariables,
      inStartYear: fiscalYearRange[0],
      inEndYear: fiscalYearRange[fiscalYearRange.length - 1]
    }));
    // The fiscal year slider does not
    // apply to the over time and per
    // capita maps.
    setUpdates({
      isFiscalYears: true,
      isCalendarYear: false,
      isRegion: false,
      isOther: false,
      isLabour: false,
      isNonLabour: false
    });
  };

  // Calendar year time slider
  const [calendarYrs, setCalendarYrs] = useState<number[]>([]);
  const [calendarYear, setCalendarYear] = usePersistedState<number>('calendarYear', currentYear);
  const handleYearChange = (
    event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    setCalendarYear(newValue as number);
  };

  const handleYearSet = (event: React.SyntheticEvent | Event, value: number | number[]) => {
    setQueryVariables(queryVariables => ({
      ...queryVariables,
      inCalendarYear: calendarYear
    }));
    // The calendar year slider only
    // applies to the per capita map
    setUpdates({
      isFiscalYears: false,
      isCalendarYear: true,
      isRegion: false,
      isOther: false,
      isLabour: false,
      isNonLabour: false
    });
  };

  // SA4/State map feature hover style
  const hoverStyle = new Style({
    stroke: new Stroke({
      color: '#6666FF',
      width: 2
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.0)',
    })
  });

  // Sa4/State map layer hover feature popup
  let sa4Selection = {};
  let stateSelection = {};
  map.on('pointermove', (e) => {
    const layers = map.getLayers();
    if ([0, 2, 3].indexOf(mapTabIndex.current) >= 0) {
      stateSelection = {};
      layers.item(4).changed();
      (layers.item(1) as VectorTileLayer).getFeatures(e.pixel).then((features) => {
        if (!features.length) {
          infoContainer.current!.style.visibility = 'hidden';
          sa4Selection = {};
          layers.item(3).changed();
          return;
        }

        const feature = features[0];
        if (!feature) {
          return;
        } else {
          infoContainer.current!.style.left = e.pixel[0] + 'px';
          infoContainer.current!.style.top = e.pixel[1] + 'px';
          infoContainer.current!.style.visibility = 'visible';
          if (mapTabIndex.current === 0) {
            infoContainer.current!.innerText = `SA4 region: ${feature.get('sa4_name21')}\nInvestment: ${formatValue(feature.get('value'), 'currency')}`;
          } else if (mapTabIndex.current === 2) {
            infoContainer.current!.innerText = `SA4 region: ${feature.get('sa4_name21')}\nChange in investment: ${formatValue(feature.get('value'), 'percent')}\n4 years to June ${currentYear}: ${formatValue(feature.get('prev'), 'currency')}\n4 years from July ${currentYear}: ${formatValue(feature.get('next'), 'currency')}`;
          } else if (mapTabIndex.current === 3) {
            infoContainer.current!.innerText = `SA4 region: ${feature.get('sa4_name21')}\nYear: ${queryVariables.inCalendarYear}\nPopulation: ${feature.get('pop')}\nInvestment: ${formatValue(feature.get('inv'), 'currency')}\nInvestment per capita: ${formatValue(feature.get('value'), 'currency')}`;
          }
        }

        sa4Selection = {};
        const fid = feature.getId();
        // @ts-ignore
        sa4Selection[fid] = feature;
        layers.item(3).changed();
      });
    } else if (mapTabIndex.current == 1) {
      sa4Selection = {};
      layers.item(3).changed();
      (layers.item(2) as VectorTileLayer).getFeatures(e.pixel).then((features) => {
        if (!features.length) {
          infoContainer.current!.style.visibility = 'hidden';
          stateSelection = {};
          layers.item(4).changed();
          return;
        }

        const feature = features[0];
        if (!feature) {
          return;
        } else {
          infoContainer.current!.style.left = e.pixel[0] + 'px';
          infoContainer.current!.style.top = e.pixel[1] + 'px';
          infoContainer.current!.style.visibility = 'visible';
          infoContainer.current!.innerText = `State: ${feature.get('ste_abbr')}\nInvestment: ${formatValue(feature.get('value'), 'currency')}`;
        }

        stateSelection = {};
        const fid = feature.getId();
        // @ts-ignore
        stateSelection[fid] = feature;
        layers.item(4).changed();
      });
    }
  });

  // When the apply button is clicked,
  // update all the components
  const applyUpdates = (id: string, values: string[]) => {
    let value;
    if (values[0] === 'ALL') {
      value = '';
    } else {
      value = values.join('|');
    }
    setQueryVariables(queryVariables => ({
      ...queryVariables,
      [id]: value
    }));

    // The region filters apply to
    // all maps and charts. The other 
    // filters do not apply to the 
    // labour charts.
    setUpdates({
      isFiscalYears: false,
      isCalendarYear: false,
      isRegion: id === 'inState' || id === 'inLocationType' ? true : false,
      isOther: id !== 'inState' && id !== 'inLocationType' ? true : false,
      isLabour: (id === 'inLvl1Group' || id === 'inResourceGroup') && tabChartIndex === 1 ? true : false,
      isNonLabour: (id === 'inPLEMGroup' || id === 'inResourceGroup') && tabChartIndex === 2 ? true : false
    });
  };

  useEffect(() => {
    map.setTarget(mapContainer.current);
  }, []);

  useEffect(() => {
    if (allMapListsStatus == 'error') {
      console.log(allMapListsError);
    } else if (allMapListsStatus == 'success' && allMapListsFetchStatus == 'idle') {
      setListsDisabled(false);
    }
  }, [allMapLists, allMapListsStatus, allMapListsFetchStatus, allMapListsError]);

  useEffect(() => {
    if (fiscalYearsStatus == 'error') {
      console.log(fiscalYearsError);
    } else if (fiscalYearsStatus == 'success' && fiscalYearsFetchStatus == 'idle') {
      // @ts-ignore
      const records = fiscalYears.executeRetrieveFiscalYears;

      const calendarYrs: number[] = [];
      var calendarYear = records.find(item => { return item.Type === 'calendar_year' });
      for (let yr = calendarYear.Min; yr <= calendarYear.Max; yr++) {
        calendarYrs.push(yr);
      }
      setCalendarYrs(calendarYrs);

      const fiscalYrs: number[] = [];
      var fiscalYear = records.find(item => { return item.Type === 'fiscal_year' });
      for (let yr = fiscalYear.Min; yr <= fiscalYear.Max; yr++) {
        fiscalYrs.push(yr);
      }
      setFiscalYrs(fiscalYrs);
      if (fiscalYearRange.length === 0) {
        setFiscalYearRange([fiscalYrs[0], fiscalYrs[fiscalYrs.length - 1]]);
      }
    }
  }, [fiscalYears, fiscalYearsStatus, fiscalYearsFetchStatus, fiscalYearsError]);

  useEffect(() => {
    if (stateSa4MapInvestmentStatus == 'error') {
      console.log(stateSa4MapInvestmentError);
    } else if (stateSa4MapInvestmentStatus == 'success' && stateSa4MapInvestmentFetchStatus == 'idle') {
      // @ts-ignore
      const records = stateSa4MapInvestmentValues.executeRetrieveStateSA4MapInvestment;

      stateMapInvestmentValuesRef.current = [];
      records.reduce((res, value) => {
        if (!res[value.State]) {
          res[value.State] = { State: value.State, Value: 0 };
          stateMapInvestmentValuesRef.current.push(res[value.State])
        }
        res[value.State].Value += value.Value;
        return res;
      }, {});

      sa4MapInvestmentValuesRef.current = [];
      records.reduce((res, value) => {
        if (value.LocationType !== 'Unknown') {
          if (!res[value.LocationType]) {
            res[value.LocationType] = { LocationType: value.LocationType, Value: 0 };
            sa4MapInvestmentValuesRef.current.push(res[value.LocationType])
          }
          res[value.LocationType].Value += value.Value;
        }
        return res;
      }, {});

      if (mapTabIndex.current == 0 || mapTabIndex.current == 1) {
        setLayerVisibility();
      }
      setSa4MapTab(false);
      setStateMapTab(false);
    }
  }, [stateSa4MapInvestmentValues, stateSa4MapInvestmentStatus, stateSa4MapInvestmentFetchStatus, stateSa4MapInvestmentError]);

  useEffect(() => {
    if (sa4OverTimeStatus == 'error') {
      console.log(sa4OverTimeError);
    } else if (sa4OverTimeStatus == 'success' && sa4OverTimeFetchStatus == 'idle') {
      // @ts-ignore
      sa4OverTimeValuesRef.current = sa4OverTimeValues.executeRetrieveSA4OverTimeMapInvestment;
      if (mapTabIndex.current == 2) {
        setLayerVisibility();
      }
      setOverTimeMapTab(false);
    }
  }, [sa4OverTimeValues, sa4OverTimeStatus, sa4OverTimeFetchStatus, sa4OverTimeError]);

  useEffect(() => {
    if (sa4PerCapitaStatus == 'error') {
      console.log(sa4PerCapitaError);
    } else if (sa4PerCapitaStatus == 'success' && sa4PerCapitaFetchStatus == 'idle') {
      // @ts-ignore
      sa4PerCapitaValuesRef.current = sa4PerCapitaValues.executeRetrieveSA4PerCapitaMapInvestment;
      if (mapTabIndex.current == 3) {
        setLayerVisibility();
      }
      setPerCapitaMapTab(false);
    }
  }, [sa4PerCapitaValues, sa4PerCapitaStatus, sa4PerCapitaFetchStatus, sa4PerCapitaError]);

  useEffect(() => {
    !sa4MapTab && !stateMapTab && fiscalYrs.length > 0 ? setFiscalYearsSlider(false) : setFiscalYearsSlider(true);
    !perCapitaMapTab && calendarYrs.length > 0 ? setCalendarYearsSlider(false) : setCalendarYearsSlider(true);
  }, [sa4MapTab, stateMapTab, perCapitaMapTab, fiscalYrs, calendarYrs]);

  useEffect(() => {
    if (queryVariables && updates && isMounted.current) {
      updateComponents();
    }
  }, [queryVariables, updates]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    }
  }, []);

  return (<Stack style={{ position: "relative", width: 1020, margin: "0 auto", display: "flex", flexDirection: "column", justifyContent: "center" }}>
    <Box className='tabBox-root'>
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <TabContext value={tabMapIndex}>
            <Box className='tabSelectorBox'>
              <TabList onChange={handleMapChange} indicatorColor="secondary" textColor="inherit">
                <Tab label="SA4 investment" value={0} key={0} disabled={sa4MapTab} />
                <Tab label="State investment" value={1} key={1} disabled={stateMapTab} />
                <Tab label="SA4 investment over time" value={2} key={2} disabled={overTimeMapTab} />
                <Tab label="SA4 investment per capita" value={3} key={3} disabled={perCapitaMapTab} />
              </TabList>
            </Box>
            <TabPanel value={tabMapIndex} keepMounted={true} style={{ padding: '5px' }}>
              <Grid container spacing={1}>
                <Grid item>
                  <Box style={{ display: "flex", width: 200, height: 560 }}>
                    <Grid container spacing={1} direction="column">
                      <MapPlotFilterList
                        AllMapLists={allMapLists}
                        plotQueryProps={mapPlotQueryProps}
                        setPlotQueryProps={setMapPlotQueryProps}
                        filterConfigList={nationalMapFiltersConfigList}
                        listsDisabled={listsDisabled}
                        applyUpdates={applyUpdates} />
                      <Grid item>
                        <Button variant="contained" style={{ width: "100%" }} onClick={resetComponents}>Clear Filters</Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item>
                  <Box ref={mapContainer} style={{ position: "relative", width: 800, height: 560 }} onMouseLeave={handleMapLeave}>
                    <Box ref={mapLoader} style={{ position: "absolute", display: "flex", zIndex: "200", width: "100%", height: "100%", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(51, 51, 51, 0.4)" }}>
                      <Oval
                        visible={true}
                        height="80"
                        width="80"
                        color="#005670"
                        secondaryColor="#005670"
                        ariaLabel="oval-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                      />
                    </Box>
                    <Box className="ol-control" style={{ zIndex: "100", top: "3.5em", left: "0.5em" }}>
                      <IconButton aria-label="house" size="small" onClick={setDefaultView}>
                        <HouseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                    <Box ref={legendContainer} onMouseOver={handleLegendHover} style={{ position: "absolute", zIndex: "101", top: "5px", right: "5px", width: "auto", height: "auto", display: "inline-block", backgroundColor: "rgba(51, 51, 51, 0.6)", color: "#fff", borderRadius: "4px", padding: "5px" }}>
                      <Box style={{ display: "flex", gap: "5px", fontWeight: 500, fontSize: "0.875rem", fontFamily: "ProximaNova" }}>{legendHeading}</Box>
                      <Box style={{ display: bins[0] ? 'flex' : 'none', gap: "5px", fontWeight: 500, fontSize: "0.875rem", fontFamily: "ProximaNova" }}>
                        <Box sx={{ width: 20, height: 20, backgroundColor: "#b3e0a6", border: "0.7px solid black" }}></Box><Box>{bins[0]}</Box>
                      </Box>
                      <Box style={{ display: bins[1] ? 'flex' : 'none', gap: "5px", fontWeight: 500, fontSize: "0.875rem", fontFamily: "ProximaNova" }}>
                        <Box sx={{ width: 20, height: 20, backgroundColor: "#ffdf8e", border: "0.7px solid black" }}></Box><Box>{bins[1]}</Box>
                      </Box>
                      <Box style={{ display: bins[2] ? 'flex' : 'none', gap: "5px", fontWeight: 500, fontSize: "0.875rem", fontFamily: "ProximaNova" }}>
                        <Box sx={{ width: 20, height: 20, backgroundColor: "#eeab26", border: "0.7px solid black" }}></Box><Box>{bins[2]}</Box>
                      </Box>
                      <Box style={{ display: bins[3] ? 'flex' : 'none', gap: "5px", fontWeight: 500, fontSize: "0.875rem", fontFamily: "ProximaNova" }}>
                        <Box sx={{ width: 20, height: 20, backgroundColor: "#ff3b29", border: "0.7px solid black" }}></Box><Box>{bins[3]}</Box>
                      </Box>
                      <Box style={{ display: bins[4] ? 'flex' : 'none', gap: "5px", fontWeight: 500, fontSize: "0.875rem", fontFamily: "ProximaNova" }}>
                        <Box sx={{ width: 20, height: 20, backgroundColor: "#aa0f40", border: "0.7px solid black" }}></Box><Box>{bins[4]}</Box>
                      </Box>
                    </Box>
                    <Box ref={infoContainer} style={{ position: "absolute", zIndex: "102", width: "auto", height: "auto", display: "inline-block", visibility: "hidden", pointerEvents: "none", backgroundColor: "#333", color: "#fff", borderRadius: "4px", padding: "5px", left: "50%", transform: "translateX(3%)", fontWeight: 500, fontSize: "0.875rem", fontFamily: "ProximaNova" }}></Box>
                    <Box style={{
                      position: "absolute", zIndex: "103", top: "5px", left: "50%", transform: "translateX(-50%)", width: "auto", height: "auto", display: "inline-block"
                    }}>
                      {alertOptions.show && (
                        <Alert severity={alertOptions.type}>
                          {alertOptions.content}
                        </Alert>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </TabPanel>
          </TabContext>
        </Grid>
        <Grid item>
          <Box ref={fiscalYearRangeContainer} sx={{ display: "flex", width: 1000, pt: 5 }}>
            <Typography id="input-slider" gutterBottom noWrap sx={{ width: 100 }}>
              Fiscal Year
            </Typography>
            <Slider ref={fiscalYearRangeRef}
              getAriaLabel={() => "Fiscal Year Range"}
              value={fiscalYearRange}
              onChange={handleYearRangeChange}
              onChangeCommitted={handleYearRangeSet}
              valueLabelDisplay="on"
              disableSwap
              step={1}
              marks
              min={fiscalYrs[0]}
              max={fiscalYrs[fiscalYrs.length - 1]}
              disabled={fiscalYearsSlider}
            />
          </Box>
          <Box ref={calendarYearContainer} sx={{ display: "none", width: 1000, pt: 5 }}>
            <Typography id="input-slider" gutterBottom noWrap sx={{ width: 130 }}>
              Calendar Year
            </Typography>
            <Slider ref={calendarYearRef}
              getAriaLabel={() => "Calendar Year"}
              value={calendarYear}
              onChange={handleYearChange}
              onChangeCommitted={handleYearSet}
              valueLabelDisplay="on"
              disableSwap
              step={1}
              marks
              min={calendarYrs[0]}
              max={calendarYrs[calendarYrs.length - 1]}
              disabled={calendarYearsSlider}
              track={false}
            />
          </Box>
          <Box ref={emptyYearContainer} sx={{ display: "none", width: 1000, height: 0 }}></Box>
          <Box sx={{ fontStyle: "italic", fontWeight: 700, mt: 2, fontFamily: "ProximaNova-Bold" }}>
            {/** The following code is a bit ugly due to some of the text having
             * HTML. I tried using the html-react-parser library, but that fails to
             * build corectly on the dev/production environments.
             * https://github.com/remarkablemark/html-react-parser/issues/1725
             */}
            {
              [0, 1, 2].indexOf(mapTabIndex.current) > -1 &&
              <strong>{caveat}</strong>
            }
            {
              mapTabIndex.current === 3 &&
              <strong>Baseline pipeline based on data collected as of Q3 2025 excl unknown SA4 projects. Next data refresh: Q2 2026.
                <br /><a target="_blank" href="https://cepar.edu.au/projections-australias-regional-and-remote-populations">Population calculated based on Projections for Australia's regional and remote population | CEPAR 2022.</a></strong>
            }
          </Box>
        </Grid>
        <Grid item>
          <TabContext value={tabChartIndex}>
            <Box className='tabSelectorBox'>
              <TabList onChange={handleChartChange} indicatorColor="secondary" textColor="inherit">
                <Tab label="Investment" value={0} key={0} disabled={investmentTab} />
                <Tab label="Labour shortage" value={1} key={1} disabled={labourTab} />
                <Tab label="Non-labour demand" value={2} key={2} disabled={nonLabourTab} />
              </TabList>
            </Box>
            <TabPanel value={0} keepMounted={true} style={{ padding: '5px' }}>
              <InvestmentChart queryVariables={queryVariables} setInvestmentTab={setInvestmentTab} updateInvestmentCharts={updateInvestmentCharts} updateAlert={updateAlert} />
            </TabPanel>
            <TabPanel value={1} keepMounted={true} style={{ padding: '5px' }}>
              <LabourShortageChart queryVariables={queryVariables} allMapLists={allMapLists} setLabourPlotQueryProps={setLabourPlotQueryProps} labourPlotQueryProps={labourPlotQueryProps} applyUpdates={applyUpdates} setLabourTab={setLabourTab} updateLabourCharts={updateLabourCharts} updateAlert={updateAlert} />
            </TabPanel>
            <TabPanel value={2} keepMounted={true} style={{ padding: '5px' }}>
              <NonLabourDemandChart queryVariables={queryVariables} allMapLists={allMapLists} setNonLabourPlotQueryProps={setNonLabourPlotQueryProps} nonLabourPlotQueryProps={nonLabourPlotQueryProps} applyUpdates={applyUpdates} setNonLabourTab={setNonLabourTab} updateNonLabourCharts={updateNonLabourCharts} updateAlert={updateAlert} />
            </TabPanel>
          </TabContext>
        </Grid>
      </Grid>
    </Box>
  </Stack>);
}